import { isString, uniq, isNil } from 'lodash-es';
import { getAdjustedFormatterValueWithDecimal } from '@/common/utils/chartUnit.utils';
import { CountUnitConverter } from '../base/CountUnitConverter';
import { UnitOverride, ValueOptions } from '../types';
import { UnitConverter } from '../base/UnitConverter';
import { AllUnitOptionValues, UnitCategoryByOption } from '../constants/unit.options';
import { GeneralUnitConverter } from '../converters/general';
import { UnitConverterFactory } from '../factory/UnitConverterFactory';
import { CountPolicies, CountPolicy } from '../policy';

// 숫자로 변환 가능한 입력만 통과시키는 게이트. 변환 불가 입력(NaN / Infinity / nil / 비-숫자
// 문자열·객체)은 throw 대신 null sentinel을 반환한다. 비숫자 값이 정상 입력인 시리즈(문자열
// 컬럼 등)에서 데이터 포인트마다 Error 객체를 할당·throw하지 않기 위함.
const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  // number/string이 아닌 입력(객체·nil 등)은 변환 대상이 아님
  return null;
};

// float64가 손실 없이 담는 십진 유효숫자 한계(약 15.95자리) — 초과 시 round-trip에서 자릿수 유실.
const PRECISION_SAFE_SIG_DIGITS = 15;

// float64로 왕복(round-trip) 시 자릿수가 유실되는 숫자 문자열인지 판별한다(정수·소수 모두).
// (예: API가 90자리 정수, 또는 유효숫자 15자리 초과 소수를 문자열로 내려주는 경우)
// 이 입력만 big.js 콜드경로(드문 입력 전용 경로)로 처리한다. 지수표기는 대상 외(API 미사용).
const isPrecisionUnsafeNumericString = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  // 십진 정수/소수만 통과 — 이 정규식이 BigInt()/Big() 입력 유효성을 보장한다.
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) return false;
  // 정수는 float64 안전 정수 경계로 정확히 판정
  if (!trimmed.includes('.')) {
    return BigInt(trimmed.replace(/^[+-]/, '')) > BigInt(Number.MAX_SAFE_INTEGER);
  }
  // 소수는 유효숫자(선행·후행 0 제외) 개수로 판정 — 15자리 초과면 float64에서 손실
  const sigDigits = trimmed
    .replace(/^[+-]/, '')
    .replace('.', '')
    .replace(/^0+/, '')
    .replace(/0+$/, '');
  return sigDigits.length > PRECISION_SAFE_SIG_DIGITS;
};

export class ChartUnitConverter {
  private static instance: ChartUnitConverter;

  private constructor() {}

  static getInstance(): ChartUnitConverter {
    if (!ChartUnitConverter.instance) {
      ChartUnitConverter.instance = new ChartUnitConverter();
    }
    return ChartUnitConverter.instance;
  }

  static convertValueWithUnitOverrides(
    value: unknown,
    unitOverride: UnitOverride | null,
    defaultUnit: AllUnitOptionValues,
    countPolicy?: CountPolicy,
  ): { quantity: unknown; countUnit: string; unit: string } {
    const numValue = toFiniteNumber(value);

    // 숫자로 변환 못한 입력(문자열·NaN·Infinity·nil 등)은 변환 없이 원본 그대로 패스
    if (numValue === null) {
      return ChartUnitConverter.buildPassthroughResult(value, unitOverride, defaultUnit);
    }

    try {
      // 오버라이드 미사용이면 기본 포맷팅
      if (!unitOverride || !unitOverride.use) {
        const converter = UnitConverterFactory.getInstance(defaultUnit);
        return converter.convertToLargerBestUnit(numValue, defaultUnit, countPolicy);
      }

      const { overrideUnit, autoScaleUnit, unitScaleTo } = unitOverride.item;
      const converter = UnitConverterFactory.getInstance(overrideUnit);

      if (overrideUnit === 'none') {
        return UnitConverter.getNoneResult(numValue, countPolicy);
      }

      if (autoScaleUnit) {
        return converter.convertToBestUnit(numValue, overrideUnit, countPolicy);
      }

      if (unitScaleTo) {
        return converter.convertToUnit(numValue, overrideUnit, unitScaleTo, countPolicy);
      }

      return converter.convertToUnit(numValue, overrideUnit, overrideUnit, countPolicy);
    } catch {
      // 변환기 단계의 예기치 못한 예외에 대한 방어 — 원본 그대로 패스
      return ChartUnitConverter.buildPassthroughResult(value, unitOverride, defaultUnit);
    }
  }

  // 숫자 변환 불가/예외 입력을 단위 정보와 함께 원본 그대로 반환
  private static buildPassthroughResult(
    value: unknown,
    unitOverride: UnitOverride | null,
    defaultUnit: AllUnitOptionValues,
  ): { quantity: unknown; countUnit: string; unit: string } {
    if (isString(value) && unitOverride?.use) {
      const { overrideUnit, autoScaleUnit, displayUnit } = unitOverride.item;
      if (displayUnit) {
        if (UnitConverter.isUnitScaleToInvalid(overrideUnit)) {
          return { quantity: value, countUnit: '', unit: displayUnit };
        }
        if (overrideUnit && !autoScaleUnit) {
          return { quantity: value, countUnit: '', unit: overrideUnit };
        }
      }
    }
    return { quantity: value, countUnit: '', unit: defaultUnit };
  }

  // 정밀도 비안전 숫자 문자열(큰 정수·고정밀 소수)을 float64로 떨어뜨리지 않고 big.js로 변환·포맷한다.
  // 변환 대상이 아니거나(일반 값) 라우팅 불가(±Infinity 초과) 시 null을 반환해
  // 기존 native 경로/패스스루로 위임한다.
  private static tryFormatPreciseNumber(
    value: unknown,
    unitOverride: UnitOverride | null,
    defaultUnit: AllUnitOptionValues,
    options: ValueOptions,
    countPolicy?: CountPolicy,
  ): { quantity: string; countUnit: string; unit: string } | null {
    if (!isPrecisionUnsafeNumericString(value)) return null;

    const bigStr = value.trim().replace(/^\+/, '');
    const numValue = Number(bigStr);
    // float64 표현 불가(>~1.8e308): 크기 기반 단위 라우팅 자체가 불가 → null로 위임(패스스루)
    if (!Number.isFinite(numValue)) return null;

    // 단위·카운트 단위 "라우팅"은 기존 number 파이프라인을 그대로 재사용한다.
    // 큰 값에서도 크기 기반 단위 선택은 float64로 정확하다 — 자릿수만 유실될 뿐 크기(magnitude)는 보존된다.
    const routed = this.convertValueWithUnitOverrides(
      numValue,
      unitOverride,
      defaultUnit,
      countPolicy,
    );
    if (typeof routed.quantity !== 'number') return null;

    // 라우팅이 확정한 목표 단위·카운트 단위의 비율로 "크기"만 big.js로 정밀 재계산한다.
    const overrideUnit = unitOverride?.item.overrideUnit ?? '';
    const fromUnit =
      unitOverride?.use && overrideUnit && overrideUnit !== 'none' ? overrideUnit : defaultUnit;
    const converter = UnitConverterFactory.getInstance(fromUnit);

    // 카운트 변환이 적용된 경우에만 countRatio를 넘긴다(미적용이면 undefined → div 생략).
    const countRatio = converter.isCountConversionEnabled(countPolicy)
      ? CountUnitConverter.getInstance().getUnitRatio(routed.countUnit)
      : undefined;

    const quantity = UnitConverter.formatBigQuantity(
      bigStr,
      {
        fromRatio: converter.getUnitRatio(fromUnit),
        toRatio: converter.getUnitRatio(routed.unit),
        countRatio,
      },
      options,
    );

    return { quantity, countUnit: routed.countUnit, unit: routed.unit };
  }

  static formatValueWithUnitOverrides(
    value: unknown,
    unitOverride: UnitOverride | null,
    defaultUnit: AllUnitOptionValues,
    _options: ValueOptions = {},
    countPolicy?: CountPolicy,
  ): string {
    const options: ValueOptions = {
      fractionDigits: _options.fractionDigits,
      convertToLocaleString: _options.convertToLocaleString ?? true,
      preserveDecimalZeros: _options.preserveDecimalZeros ?? false,
      roundingMode: _options.roundingMode ?? 0, // NOTE: 기본값은 RoundDown
    };

    // 정밀도 비안전 숫자 문자열(예: API의 90자리 정수·고정밀 소수)은 float64 변환 시 자릿수가
    // 유실되므로 big.js 콜드경로(드문 입력 전용 경로)로 처리한다. 라벨 선택은 아래 number 경로와 동일.
    const bigResult = ChartUnitConverter.tryFormatPreciseNumber(
      value,
      unitOverride,
      defaultUnit,
      options,
      countPolicy,
    );
    if (bigResult) {
      if (
        !unitOverride?.use ||
        (!UnitConverter.isUnitScaleToInvalid(unitOverride.item.overrideUnit) &&
          unitOverride.item.autoScaleUnit)
      ) {
        return UnitConverter.formatResult({
          quantity: bigResult.quantity,
          countUnit: bigResult.countUnit,
          unit: bigResult.unit,
        });
      }
      return UnitConverter.formatResult({
        quantity: bigResult.quantity,
        countUnit: bigResult.countUnit,
        unit: bigResult.unit,
        displayUnit: unitOverride.item.displayUnit ?? '',
      });
    }

    const { quantity, countUnit, unit } = this.convertValueWithUnitOverrides(
      value,
      unitOverride,
      defaultUnit,
      countPolicy,
    );

    const { displayUnit = '' } = unitOverride?.item ?? {};

    if (typeof quantity !== 'number' || !Number.isFinite(quantity)) {
      if (
        UnitConverter.isUnitScaleToInvalid(unitOverride?.item.overrideUnit ?? '') ||
        !unitOverride?.item.autoScaleUnit
      ) {
        return [String(quantity), displayUnit].filter(Boolean).join(' ');
      }
      return String(quantity);
    }

    if (
      !unitOverride?.use ||
      (!UnitConverter.isUnitScaleToInvalid(unitOverride.item.overrideUnit) &&
        unitOverride.item.autoScaleUnit)
    ) {
      return UnitConverter.formatResult({
        quantity: `${UnitConverter.formatValue(quantity, options)}`,
        countUnit,
        unit,
      });
    }
    return UnitConverter.formatResult({
      quantity: `${UnitConverter.formatValue(quantity, options)}`,
      countUnit,
      unit,
      displayUnit,
    });
  }
}

interface UnitInfo {
  baseUnit: string;
  autoScaleUnit: boolean;
  unitScaleTo: string;
  displayUnit: string;
}

export class ChartYAxisUnitConverter {
  private unitInfoList: UnitInfo[] = [];

  constructor(unitInfoList: UnitInfo[] = []) {
    this.unitInfoList = unitInfoList;
  }

  private convertWithSameCategory = true;

  private yAxisCountUnit: string = '';

  private yAxisBestUnit: string = '';

  private yMaxValue: number = 0;

  public setUnitInfoList(unitInfoList: UnitInfo[]) {
    this.unitInfoList = unitInfoList;
  }

  // y축 단위 변환 유효 여부 확인
  private isYAxisUnitConvertValid() {
    const allNoneAuto = this.unitInfoList.every(({ autoScaleUnit }) => !autoScaleUnit);
    const allAuto = this.unitInfoList.every(({ autoScaleUnit }) => autoScaleUnit);
    const baseUnitList = uniq(this.unitInfoList.map(({ baseUnit }) => baseUnit));
    const unitScaleToList = uniq(this.unitInfoList.map(({ unitScaleTo }) => unitScaleTo));

    // 모두 자동 단위 사용 안하면
    if (allNoneAuto) {
      // baseUnit이 모두 count성 단위이면 unitScaleTo는 unique 해야 한다.
      if (baseUnitList.every((unit) => GeneralUnitConverter.getInstance().isValidUnit(unit))) {
        return unitScaleToList.length === 1;
      }
      if (this.convertWithSameCategory) {
        const scaleToCategories = uniq(unitScaleToList.map((unit) => UnitCategoryByOption[unit]));
        if (scaleToCategories.length === 1) {
          return baseUnitList.length === 1;
        }
        return false;
      }
      // baseUnit이 모두 count성 단위가 아니면 baseUnit과 unitScaleTo는 모두 unique 해야한다.
      return baseUnitList.length === 1 && unitScaleToList.length === 1;
    }
    // 모두 자동 단위 사용하면 baseUnit은 unique 해야한다.
    if (allAuto) {
      return baseUnitList.length === 1;
    }
    return false;
  }

  private getYAxisBaseUnit() {
    if (this.isYAxisUnitConvertValid()) {
      return this.unitInfoList[0].baseUnit;
    }
    return '';
  }

  // y축 변환 단위 설정
  // countUnitEnabled=false면 yAxisCountUnit=''로 둬 약어 나눗셈·접미사를 끈다.
  // base→best 단위 변환(yAxisBestUnit)은 별개라 유지.
  public setYAxisBestUnit(yMaxValue: number, allUseOverride: boolean, countUnitEnabled = true) {
    if (!Number.isFinite(yMaxValue) || Number.isNaN(yMaxValue)) {
      this.yAxisBestUnit = '';
      this.yAxisCountUnit = '';
      this.yMaxValue = 0;
      return;
    }

    this.yMaxValue = yMaxValue;

    if (!this.isYAxisUnitConvertValid()) {
      // 단위 변환 유효 안하면 count 단위로 설정
      this.yAxisBestUnit = '';
      this.yAxisCountUnit = countUnitEnabled
        ? CountUnitConverter.getInstance().convertToBestUnit(yMaxValue, '').countUnit
        : '';
      return;
    }
    const allNoneAuto = this.unitInfoList.every(({ autoScaleUnit }) => !autoScaleUnit);

    if (allNoneAuto) {
      this.yAxisBestUnit = this.unitInfoList[0].unitScaleTo ?? '';
      const converter = UnitConverterFactory.getInstance(this.getYAxisBaseUnit());

      if (this.convertWithSameCategory) {
        const scaleToList = this.unitInfoList.map(({ unitScaleTo }) => unitScaleTo);
        this.yAxisBestUnit = converter.getLargestUnit(scaleToList);
      } else {
        this.yAxisBestUnit = this.unitInfoList[0].unitScaleTo ?? '';
      }
      const { countUnit } = converter.convertToUnit(
        yMaxValue,
        this.getYAxisBaseUnit(),
        this.yAxisBestUnit,
      );
      this.yAxisCountUnit = countUnitEnabled ? countUnit : '';
      return;
    }

    const converter = UnitConverterFactory.getInstance(this.getYAxisBaseUnit());

    const result = allUseOverride
      ? converter.convertToBestUnit(yMaxValue, this.getYAxisBaseUnit())
      : converter.convertToLargerBestUnit(yMaxValue, this.getYAxisBaseUnit());

    this.yAxisBestUnit = result.unit;
    this.yAxisCountUnit = countUnitEnabled ? result.countUnit : '';
  }

  public getYAxisDisplayUnit() {
    if (!this.isYAxisUnitConvertValid()) {
      return this.yAxisBestUnit;
    }
    const uniqueDisplayUnit = uniq(this.unitInfoList.map(({ displayUnit }) => displayUnit));
    if (uniqueDisplayUnit.length === 1 && !!uniqueDisplayUnit[0]) {
      return uniqueDisplayUnit[0];
    }
    return UnitConverter.getUnitAbbreviation(this.yAxisBestUnit);
  }

  // fallbackFractionDigits: getOptions() 가 fractionDigits 를 주지 않을 때 quantity>=1 에 적용할 기본 자리수.
  // 기본 3(기존 축 라벨 동작). null 로 주면 fallback 없이 formatValue 의 원본 정밀도 경로를 탄다(임계선 라벨용).
  // ※ null 이어도 마지막 getAdjustedFormatterValueWithDecimal 이 소수 4자리로 반올림하므로 최종 상한은 4자리다.
  //    임계값 입력(ThresholdsSetting 의 ev-input-number precision=4)과 동일한 상한이라 입력값은 손실 없이 표시된다.
  // ※ 기본 파라미터 특성상 undefined 를 넘기면 기본값 3 이 적용되므로, "fallback 없음"은 반드시 null 로 표현한다.
  public getYAxisFormatter(
    getOptions: () => ValueOptions,
    fallbackFractionDigits: number | null = 3,
  ) {
    return (value: number) => {
      let formattedNumber = '';

      try {
        if (!Number.isFinite(value)) return value;
        const { fractionDigits } = getOptions();

        // case1: 단위 변환 유효 False
        if (!this.isYAxisUnitConvertValid()) {
          const { quantity } = CountUnitConverter.getInstance().convertToUnit(
            value,
            '',
            this.yAxisCountUnit,
          );

          formattedNumber = UnitConverter.formatValue(quantity, {
            ...getOptions(),
            fractionDigits:
              quantity >= 1
                ? (fractionDigits ?? fallbackFractionDigits ?? undefined)
                : (fractionDigits ?? undefined),
          });
        } else {
          // case2: 단위 변환 유효 True
          const converter = UnitConverterFactory.getInstance(this.getYAxisBaseUnit());

          // value: y축에 들어온 원시 값
          // quantity: value를 baseUnit -> best Unit으로 반환한 결과
          const { quantity } = converter.convertToUnit(
            value,
            this.getYAxisBaseUnit(),
            this.yAxisBestUnit,
            CountPolicies.countConversionDisabled,
          );

          if (typeof quantity !== 'number' || !Number.isFinite(quantity)) {
            return quantity;
          }

          // countQuantity: quantity에 스케일 단위(K, M, G 등)를 적용한 결과
          const { quantity: countQuantity } = CountUnitConverter.getInstance().convertToUnit(
            quantity,
            '',
            this.yAxisCountUnit,
          );

          formattedNumber = UnitConverter.formatValue(countQuantity, {
            ...getOptions(),
            fractionDigits:
              countQuantity >= 1
                ? (fractionDigits ?? fallbackFractionDigits ?? undefined)
                : (fractionDigits ?? undefined),
          });
        }

        if (!formattedNumber) {
          return formattedNumber;
        }

        // case1 + case2 : 지수 표기 확인
        return getAdjustedFormatterValueWithDecimal(
          formattedNumber + this.yAxisCountUnit,
          !isNil(fractionDigits),
        );
      } catch {
        return value;
      }
    };
  }

  public getConvertedValueWithUnit() {
    return (value: number, isReverse: boolean = false): number => {
      try {
        if (!Number.isFinite(value)) return value;

        if (!this.isYAxisUnitConvertValid()) {
          const { quantity } = CountUnitConverter.getInstance().convertToUnit(
            value,
            '',
            this.yAxisCountUnit,
          );

          return quantity;
        }

        const baseUnit = isReverse ? this.yAxisBestUnit : this.getYAxisBaseUnit();
        const bestUnit = isReverse ? this.getYAxisBaseUnit() : this.yAxisBestUnit;

        const converter = UnitConverterFactory.getInstance(baseUnit);

        const { quantity } = converter.convertToUnit(
          value,
          baseUnit,
          bestUnit,
          CountPolicies.countConversionDisabled,
        );

        if (typeof quantity !== 'number' || !Number.isFinite(quantity)) {
          return value;
        }

        return quantity;
      } catch {
        return value;
      }
    };
  }

  static getUnitInfo(unitOverride: UnitOverride | undefined, defaultUnit: string): UnitInfo {
    if (!unitOverride || !unitOverride.use) {
      return { baseUnit: defaultUnit, displayUnit: '', autoScaleUnit: true, unitScaleTo: '' };
    }
    const { overrideUnit, autoScaleUnit, unitScaleTo, displayUnit } = unitOverride.item;
    if (UnitConverter.isUnitScaleToInvalid(overrideUnit)) {
      // 'none'인 경우 기본 단위 사용
      if (overrideUnit === 'none') {
        return { baseUnit: defaultUnit, displayUnit, autoScaleUnit, unitScaleTo: defaultUnit };
      }
      // 다른 invalid 단위인 경우 해당 단위를 baseUnit과 unitScaleTo로 사용
      return { baseUnit: overrideUnit, displayUnit, autoScaleUnit, unitScaleTo: overrideUnit };
    }
    if (!autoScaleUnit && unitScaleTo) {
      return { baseUnit: overrideUnit, displayUnit, autoScaleUnit, unitScaleTo };
    }
    return { baseUnit: overrideUnit, displayUnit: '', autoScaleUnit, unitScaleTo };
  }
}
